import {
  Button,
  Flex,
  MagnifyingGlassIcon,
  Panel,
  PlusIcon,
  ProgressCircle,
  Text,
  TextField,
} from 'components'
import { useCurrentUser } from 'components/providers'
import { useState } from 'react'
import { generatePath, useNavigate } from 'react-router'
import { createDoc, FetchStatus, useBreakpoint, usePrefCurrentWorkspace, useTheme } from 'utils'
import { RoutePath } from '../../utils/routing'
import { makeEmptyTool, ToolRecord } from '../../utils/toolUtils'
import ContentTile from '../ContentTile'

interface Props {
  status: FetchStatus
  tools: ToolRecord[]
}

export default function ToolContentList({ status, tools }: Props) {
  const [workspaceId] = usePrefCurrentWorkspace()
  const navigate = useNavigate()
  const user = useCurrentUser()
  const { theme } = useTheme()
  const [search, setSearch] = useState('')
  const isMobile = useBreakpoint() < 1

  function performSearch(val: string): void {
    setSearch(val)
  }

  async function createNewTool() {
    const { id } = await createDoc(user!.id, 'tools', makeEmptyTool(workspaceId!))
    navigate(generatePath(RoutePath.ToolInfo, { workspaceId: workspaceId!, id }))
  }

  if (status === 'loading')
    return (
      <Flex p={20} justifyContent="center" maxW={'100%'}>
        <ProgressCircle isIndeterminate />
      </Flex>
    )

  if (!tools)
    return (
      <Flex p={20} justifyContent="center" maxW="100%">
        <Text>No tools available</Text>
      </Flex>
    )

  return (
    <Flex pb={3} pr={isMobile ? 0 : 5} direction="column" flex={1} gap={2} overflow="auto">
      <Flex direction="row" gap={2} justify="space-between" px={1}>
        <TextField
          disabled={tools.length === 0}
          onChange={e => performSearch(e.target.value)}
          leftElement={
            <MagnifyingGlassIcon color={theme === 'light' ? 'rgba(23, 23, 23, 0.6)' : 'white'} />
          }
          placeholder="Search..."
          value={search}
          width={{ base: '200px', sm: '300px' }}
          boxShadow={'0px 1px 4px 0px rgba(0, 0, 0, 0.06)'}
          backdropFilter={'blur(14px)'}
          bg={theme === 'light' ? 'rgba(255, 255, 255, 0.74)' : 'rgba(255, 255, 255, 0.14)'}
          borderRadius="sm"
          borderWidth={0}
          pl={10}
        />
        <Panel>
          <Button leftIcon={<PlusIcon />} variant="ghost" onClick={() => createNewTool()}>
            Add Tool
          </Button>
        </Panel>
      </Flex>

      <Flex direction="column" gap={2}>
        {tools
          .filter(item => item.title?.toLowerCase().includes(search.toLowerCase()))
          .map((item, index) => (
            <ContentTile
              key={index}
              onEdit={() =>
                navigate(
                  generatePath(RoutePath.ToolInfo, {
                    workspaceId: workspaceId!,
                    id: item.id,
                  })
                )
              }
              title={item.title}
              description={item.url}
            />
          ))}
      </Flex>
    </Flex>
  )
}
